// A searchable HollowScript reference beside the code, built from the same
// catalog the editor's completion and hover read (never a second, hand-
// written copy): every capability this project can `uses`, with whether
// it is granted right now, its members' signatures (the native checker's own
// type text, from the native capability declarations) and what they do; the
// standard library by receiver; and the keyword list, from the lexer's own
// table. Clicking a
// member inserts a call at the caret. Godot's "Search Help" panel and
// Roblox's API docs pop-ups are the reference points; ADR-0014 §3 of the
// HollowScript repo makes "the granted capability set visible to the
// author" a requirement of any HollowScript editor, and this is where it is
// visible.
import { useMemo, useState } from 'react';

import { STUDIO_CAPABILITY_NAMES, type StudioCapabilityName } from '../../../engine/scripting/capabilityNames';
import {
  CAPABILITY_DESCRIPTIONS,
  grantedCapabilityNames,
  isAlwaysOnCapability,
  STDLIB_FREE_FUNCTIONS,
  STDLIB_FREE_RECEIVERS,
  STDLIB_METHODS,
  stdlibMemberName,
  type CapabilityCatalog,
  type StdlibMember,
} from '../../../engine/scripting/monaco';
import { insertIntoActiveScript } from '../scripting/editorCommands';
import { CodeIcon } from './codeIcons';
import { CwSearchInput, CwSectionLabel } from './codeShared';
import { useCapabilityCatalog } from './useCapabilityCatalog';
import { useProjectScriptCapabilities } from './useProjectScriptCapabilities';

const RECEIVER_ORDER = ['String', 'List', 'Number', 'Record', 'Result'];

function matches(needle: string, ...haystack: string[]): boolean {
  if (!needle) return true;
  return haystack.some((h) => h.toLowerCase().includes(needle));
}

/** `effect entities.spawn(` for a member, `effect print(` for a bare
 * capability, `.length()` for a method, `round(` for a free function - the
 * caret lands where the first argument goes. */
function insertionFor(kind: 'capability' | 'member' | 'method' | 'free', owner: string, signature: string): string {
  const name = signature.slice(0, signature.indexOf('('));
  const hasArgs = !/\(\s*\)/.test(signature);
  const close = hasArgs ? '' : ')';
  switch (kind) {
    case 'capability':
      return `effect ${owner}(${close}`;
    case 'member':
      return `effect ${owner}.${name}(${close}`;
    case 'method':
      return `.${name}(${close}`;
    default:
      return `${name}(${close}`;
  }
}

function CapabilityGroup({ catalog, name, granted, needle }: { catalog: CapabilityCatalog; name: StudioCapabilityName; granted: boolean; needle: string }) {
  const entry = catalog.entries.get(name);
  const members = entry?.members ?? [];
  const description = CAPABILITY_DESCRIPTIONS[name];
  const functionSignature = entry?.kind === 'function' ? entry.signature : undefined;
  const visibleMembers = members.filter((m) => matches(needle, m.name, m.signature, name));
  if (!matches(needle, name, description) && visibleMembers.length === 0) return null;
  const alwaysOn = isAlwaysOnCapability(name);
  return (
    <div className="cw-ref-group">
      <div className="cw-ref-head">
        <span className="cw-ref-name">{name}</span>
        {alwaysOn ? (
          <span className="cw-pill cw-pill--muted" title="Always available to every script">always on</span>
        ) : granted ? (
          <span className="cw-pill cw-pill--success" title="Granted in Setup: Meta">granted</span>
        ) : (
          <span className="cw-pill cw-pill--warning" title="Not granted: turn it on under Setup, Scripting capabilities">not granted</span>
        )}
        <span style={{ marginLeft: 'auto', fontFamily: 'var(--ss-mono)', fontSize: 10, color: 'var(--ss-text-3)' }}>uses {name}</span>
      </div>
      <div className="cw-ref-desc">{description}</div>
      {functionSignature !== undefined ? (
        <button type="button" className="cw-ref-member" onClick={() => insertIntoActiveScript(insertionFor('capability', name, functionSignature))} title="Insert at the caret">
          <span className="cw-ref-sig">{functionSignature}</span>
        </button>
      ) : (
        visibleMembers.map((member) => (
          <button key={member.name} type="button" className="cw-ref-member" onClick={() => insertIntoActiveScript(insertionFor('member', name, member.signature))} title="Insert at the caret">
            <span className="cw-ref-sig">{member.signature}</span>
          </button>
        ))
      )}
    </div>
  );
}

function StdlibGroup({ receiver, members, needle, free }: { receiver: string; members: readonly StdlibMember[]; needle: string; free: boolean }) {
  const visible = members.filter((m) => matches(needle, stdlibMemberName(m), m.signature, m.doc, receiver));
  if (visible.length === 0) return null;
  return (
    <div className="cw-ref-group">
      <div className="cw-ref-head">
        <span className="cw-ref-name">{receiver}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--ss-text-3)' }}>{free ? 'free functions' : 'methods'}</span>
      </div>
      {visible.map((member) => (
        <button
          key={member.signature}
          type="button"
          className="cw-ref-member"
          onClick={() => insertIntoActiveScript(insertionFor(free ? 'free' : 'method', receiver, member.signature))}
          title={member.doc}
        >
          <span className="cw-ref-sig">{member.signature}</span>
          <span className="cw-ref-doc">{member.doc}</span>
        </button>
      ))}
    </div>
  );
}

export function CodeReferencePanel() {
  const { capabilities } = useProjectScriptCapabilities();
  const catalog = useCapabilityCatalog();
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const granted = useMemo(() => new Set(grantedCapabilityNames(capabilities)), [capabilities]);

  const stdlibByReceiver = useMemo(() => {
    const groups = new Map<string, { members: StdlibMember[]; free: boolean }>();
    for (const member of STDLIB_METHODS) {
      // Number and record operations are called bare, like the result pair.
      const group = groups.get(member.receiver) ?? { members: [], free: STDLIB_FREE_RECEIVERS.includes(member.receiver) };
      group.members.push(member);
      groups.set(member.receiver, group);
    }
    for (const member of STDLIB_FREE_FUNCTIONS) {
      const group = groups.get(member.receiver) ?? { members: [], free: true };
      group.members.push(member);
      group.free = true;
      groups.set(member.receiver, group);
    }
    return [...groups.entries()].sort(([a], [b]) => RECEIVER_ORDER.indexOf(a) - RECEIVER_ORDER.indexOf(b));
  }, []);

  const keywords = (catalog?.keywords ?? []).filter((k) => matches(needle, k));

  return (
    <div className="cw-fill cw-fill--side">
      <div style={{ padding: '10px 10px 0', display: 'flex' }}>
        <CwSearchInput value={query} onChange={setQuery} placeholder="Search HollowScript" ariaLabel="Search the HollowScript reference" />
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 16 }}>
        <CwSectionLabel>Capabilities</CwSectionLabel>
        <div className="cw-hint" style={{ paddingTop: 0 }}>
          A script declares what it touches with <code style={{ fontFamily: 'var(--ss-mono)' }}>uses</code> and marks every such call with{' '}
          <code style={{ fontFamily: 'var(--ss-mono)' }}>effect</code>. Which engine capabilities a project grants is set under Setup.
        </div>
        {catalog ? (
          STUDIO_CAPABILITY_NAMES.map((name) => <CapabilityGroup key={name} catalog={catalog} name={name} granted={granted.has(name)} needle={needle} />)
        ) : (
          <div className="cw-hint">Loading the capability list...</div>
        )}

        <CwSectionLabel>Standard library</CwSectionLabel>
        <div className="cw-hint" style={{ paddingTop: 0 }}>Always available, never imported. String and list operations are methods; number, record and result operations are free functions.</div>
        {stdlibByReceiver.map(([receiver, group]) => <StdlibGroup key={receiver} receiver={receiver} members={group.members} needle={needle} free={group.free} />)}

        {keywords.length > 0 && (
          <>
            <CwSectionLabel count={keywords.length}>Keywords</CwSectionLabel>
            <div className="cw-ref-keywords">
              {keywords.map((keyword) => (
                <button key={keyword} type="button" className="cw-ref-keyword" onClick={() => insertIntoActiveScript(keyword + ' ')} title="Insert at the caret">
                  {keyword}
                </button>
              ))}
            </div>
          </>
        )}

        <div className="cw-hint" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <span style={{ color: 'var(--ss-text-3)', paddingTop: 1 }}>
            <CodeIcon kind="book" size={13} />
          </span>
          <span>
            HollowScript is statically typed, every value is immutable, and files end in <code style={{ fontFamily: 'var(--ss-mono)' }}>.hws</code>. Split code across
            scripts with <code style={{ fontFamily: 'var(--ss-mono)' }}>export fn</code> and <code style={{ fontFamily: 'var(--ss-mono)' }}>import name from &quot;./other&quot;</code>.
          </span>
        </div>
      </div>
    </div>
  );
}
